/**
 * Visa feature - Figma `6377:87523`.
 *
 * A large heading, a paragraph, a call to action, and a row of photographs underneath.
 * Despite sitting next to the WecoAcademy banner in the catalogue it is a different layout -
 * not the card-beside-photograph one - which is why it was checked rather than assumed.
 *
 * **`media` is repeatable here too.** The design shows two photographs; the model held
 * one. The body stays rich text: the mock-up has an inline link to the Polish foreign
 * ministry inside the paragraph, which plain text could not carry.
 *
 * **`tiles` is the other ending.** Business Travel closes the same block with a mosaic of
 * destinations rather than two photographs (`3883:87988`): a seven-column band of small
 * square tiles, most of them places, a few of them pale-blue labels - "Kierunki",
 * "Informacje ogolne" - that lead into the visa tool. A tile with an image is a
 * photograph and one without is a label, the rule `meetings-grid` already uses, so the
 * content picks the ending and there is no enum to keep in step with it.
 *
 * The tiles are `shared.mosaic-tile`, not `shared.card`: they carry a shape and an icon,
 * which the nine other sections using `card` have no use for. That is also what lets the
 * wide tile be wide - the file's "Informacje ogolne" runs across two columns.
 *
 * **The heading is Display-01**, 82px over the file's 1180, where the block ends in
 * photographs (6377:87528); over the mosaic it is h1, 60px over 968, as Business Travel
 * draws it (6377:87285). The body is body-medium over 640 either way (6377:87529).
 *
 * **The photographs share one height, and `imageWidths` splits the row**: 1-1, 2-1 or 1-2,
 * the designer's three (2026-09-26). The row is 1360x482 in the file; its own pair is 856
 * to 490, which reads as 2-1. The row sets the frames, so each picture's `aspect` is not
 * read here - two frames of their own proportions could not also be one height.
 */
import { Blocks, CtaLink, Eyebrow, SectionShell, withHighlight } from '../primitives';
import type { MosaicTile, VisaFeatureSection } from '../../types/sections';
import { HeroIcon } from '../../components/ui/HeroIcon';
import { Image, SIZES } from '../../components/ui/Image';
import { AppLink } from '../../components/ui/AppLink';
import { useSite } from '../../lib/site-context';

/** A pair's columns by `imageWidths`; anything but a pair shares the row evenly. */
function imageColumns(count: number, widths?: string): string {
  if (count !== 2) return count > 2 ? 'lg:grid-cols-3' : '';
  if (widths === 'firstWider') return 'lg:grid-cols-[2fr_1fr]';
  if (widths === 'secondWider') return 'lg:grid-cols-[1fr_2fr]';
  return 'lg:grid-cols-2';
}

/**
 * One cell of the destination mosaic (Figma `3883:87988`).
 *
 * Three shapes, and the file uses all three: a rounded square, a circle, and a wide one
 * across two columns. What fills the cell is decided by what the editor filled in rather
 * than by a fourth setting - a photograph if there is an image, otherwise an icon and a
 * label, either of which may stand alone. The file has a tile with two icons and no
 * words, one with an icon above a word, and one with nothing but an arrow.
 *
 * **The arrow is a link, so it has a label all the same** - "Kontakt" - with `hideLabel`
 * keeping it off the tile. A link is named by its label on every tile, drawn or not.
 */
function Tile({ tile }: { tile: MosaicTile }) {
  const { t } = useSite();
  const circle = tile.shape === 'circle';
  const shell = `group flex aspect-square items-center justify-center overflow-hidden ${
    circle ? 'rounded-full' : 'rounded-l'
  } ${tile.shape === 'wide' ? 'sm:col-span-2 sm:aspect-auto' : ''}`;

  if (tile.image) {
    // A photograph never draws the label. Linked, the picture is the link's whole content,
    // so its text alternative is the link's name and says where it goes - the label, which
    // the publish guard insists on - rather than what the picture shows. The file's own
    // description used to come first, and the guard sees to it that there always is one,
    // so the label it demanded was never read.
    const alt = tile.url
      ? tile.label || tile.image.alternativeText || ''
      : tile.image.alternativeText || tile.label || '';
    const photo = (
      <Image
        media={tile.image}
        alt={alt}
        sizes="(min-width: 1024px) 200px, 33vw"
        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
      />
    );
    return tile.url
      ? <AppLink to={tile.url} className={`${shell} bg-surface-sunken`}>{photo}</AppLink>
      : <figure className={`m-0 ${shell} bg-surface-sunken`}>{photo}</figure>;
  }

  // A wide tile sets its icon beside the words; a square or a circle stacks them, which
  // is what the file draws and also what fits. A hidden label stays in the tile for a
  // screen reader and leaves the layout - `sr-only` takes it out of the flow - so the
  // icon stands alone, at the size of an icon on its own.
  const shown = Boolean(tile.label) && !tile.hideLabel;
  const face = (
    <span className={`flex items-center gap-2 px-4 text-center text-h5 text-blue-900 ${
      tile.shape === 'wide' ? 'flex-row' : 'flex-col'
    }`}>
      <HeroIcon name={tile.icon} size={shown ? 24 : 32} className="transition group-hover:scale-110" />
      {tile.label ? <span className={shown ? undefined : 'sr-only'}>{tile.label}</span> : null}
    </span>
  );

  const pale = `${shell} bg-blue-50`;
  // A tile with a url and no label is a link whose whole content is an icon, and an icon
  // carries no accessible name, so the link had none: screen readers announced "link" and
  // nothing else. The file's arrow tile (3883:87988) was seeded exactly like that. The
  // label names it where there is one, hidden or not, and the publish guard refuses a link
  // tile without; where there is none all the same - a draft in preview - the generic word
  // does, because the alternative is reading out a Heroicon's file name.
  return tile.url
    ? (
      <AppLink
        to={tile.url}
        aria-label={tile.label ? undefined : t('a11y.tileLink')}
        className={`${pale} transition hover:bg-blue-100`}
      >
        {face}
      </AppLink>
    )
    : <div className={pale}>{face}</div>;
}

export default function VisaFeature({ data }: { data: VisaFeatureSection }) {
  const images = (data.media ?? []).filter((item) => item?.image);
  const tiles = data.tiles ?? [];

  return (
    <SectionShell settings={data.settings} name="visa-feature">
      {data.eyebrow ? <Eyebrow value={data.eyebrow} /> : null}
      <h2 className={`text-h2 text-ink ${tiles.length ? 'max-w-[968px] lg:text-h1' : 'max-w-[1180px] lg:text-display'}`}>
        {withHighlight(data.heading)}
      </h2>

      {/* Where the call to action sits differs with the ending, and the file is explicit
          about both: under the paragraph on Customer Care (6377:87523), out to the right
          of it on Business Travel (3883:87987). `tiles` already says which page this is. */}
      <div className={tiles.length
        ? 'flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'
        : 'flex max-w-2xl flex-col gap-8'}
      >
        {data.body ? <div className="max-w-[640px]"><Blocks value={data.body} className="text-body-md" /></div> : null}
        {data.cta ? <CtaLink value={data.cta} /> : null}
      </div>

      {tiles.length ? (
        // Seven across, as in the file; fewer as the viewport narrows, because a
        // 185px tile becomes unreadable long before a phone runs out of width.
        <div className="mt-8 grid grid-cols-3 gap-2.5 sm:grid-cols-5 lg:grid-cols-7">
          {tiles.map((tile, i) => <Tile key={i} tile={tile} />)}
        </div>
      ) : null}

      {images.length ? (
        // One height for the row, 482 at the file's 1360, and the columns decide the widths.
        // Stacked on a narrow screen, where each photograph takes its own 16:10 again.
        <div className={`mt-8 grid gap-3.5 lg:aspect-[1360/482] ${imageColumns(images.length, data.imageWidths)}`}>
          {images.map((item, i) => (
            <figure key={i} className="relative m-0 aspect-[16/10] overflow-hidden rounded-l bg-surface-sunken lg:aspect-auto">
              <Image
                media={item.image}
                alt={item.alt ?? item.image.alternativeText ?? ''}
                sizes={images.length > 1 ? SIZES.half : SIZES.full}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </figure>
          ))}
        </div>
      ) : null}
    </SectionShell>
  );
}
